import { ITextFieldProps, Stack, TextField } from "@fluentui/react";
import React, { useState } from "react";
import { Control, Controller, FieldErrors, ValidationRule } from "react-hook-form";

import { FormValidationRules } from "../../utils/types/FormValidationRules";
import { getValidate } from "../../utils/validation/getValidate";
import { FailureSeverity, ValidationFailure } from "../../utils/validation/ValidationResult";
import { TextFieldClearButton } from "../inputs";
import { FormErrorMessage } from "./FormErrorMessage";
import { FormFailureMessages } from "./FormFailureMessages";
import { getFormLabelText } from "./getFormLabelText";

export type FormNumberTextFieldProp = Pick<ITextFieldProps, "multiline"> & {
    name: string;
    label: string;
    control: Control;
    errors: FieldErrors;
    rules?: FormNumberTextFieldRules;
    disabled?: boolean;
    inputValue?: number;
    onInputChange?(inputValue: number | null): void;
};

export const FormNumberTextField: React.FunctionComponent<FormNumberTextFieldProp> = (
    props: FormNumberTextFieldProp
) => {
    const [warnings, setWarnings] = useState<ValidationFailure[]>([]);

    const clearWarnings = () => {
        setWarnings([]);
    };

    const render = (renderProp: { value: number | undefined; onChange(inputValue: number | null): void }) => {
        const handleInputChange = (
            _event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
            newValue?: string
        ) => {
            clearWarnings();

            const newNumber = parseInt(newValue ?? "");
            if (newNumber) {
                renderProp.onChange(newNumber);

                if (props.onInputChange) props.onInputChange(newNumber);
            } else {
                renderProp.onChange(null);

                if (props.onInputChange) props.onInputChange(null);
            }
        };

        const clearButtonBackgroudColor = "transparent";
        const clearButtonId = `${props.name}_TextFieldClearButtonId`;

        const clearValue = () => {
            renderProp.onChange(null);

            if (props.onInputChange) {
                props.onInputChange(null);
            }
        };

        const handleClearButtonClick = () => {
            clearValue();
            clearWarnings();
        };

        const renderClearButton = () => (
            <TextFieldClearButton
                disabled={!renderProp.value}
                onClick={handleClearButtonClick}
                id={clearButtonId}
                testId={`${props.name}-clear`}
            />
        );

        const renderSuffix = () => {
            return (
                <Stack verticalAlign="center" horizontal={true}>
                    {!!renderProp.value && renderClearButton()}
                </Stack>
            );
        };

        const inputProps = {
            ["data-testid"]: props.name,
            ["data-testtype"]: "text",
            ...renderProp
        };

        return (
            <TextField
                {...inputProps}
                name={props.name}
                label={getFormLabelText(props.label, !!props.rules?.required)}
                styles={{
                    fieldGroup: {
                        border: props.errors[props.name] && "2px solid red"
                    },
                    suffix: { padding: 0, background: clearButtonBackgroudColor, alignItems: "start" }
                }}
                onChange={handleInputChange}
                value={(props.inputValue?.toString() || renderProp.value?.toString()) ?? ""}
                onRenderSuffix={renderSuffix}
                multiline={props.multiline}
                disabled={props.disabled}
            />
        );
    };

    return (
        <Stack>
            <Controller
                name={props.name}
                control={props.control}
                rules={{
                    ...props.rules,
                    validate: getValidate<number, FormNumberTextFieldRules>(
                        props.rules,
                        props.control.formStateRef.current.submitCount,
                        setWarnings
                    )
                }}
                render={render}
            />
            <FormErrorMessage name={props.name} errors={props.errors} />
            <FormFailureMessages failures={warnings} severity={FailureSeverity.Warning} />
        </Stack>
    );
};

export interface FormNumberTextFieldRules extends FormValidationRules<number> {
    pattern?: ValidationRule<RegExp>;
}
